
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ApiResponse } from '../../_models/api-response.model';
import { CustomerReservationList, ReservationSearchCriteria } from '../../_models/reservation.model';
import { PaginationComponent } from '../../_shared_components/pagination/pagination.component';
import { ClientReservationService } from '../../_services/api/reservation-client.service';
import { MemberReservationCard } from './member-reservation-card/member-reservation-card';


@Component({
  selector: 'app-member-reservations',
  imports: [CommonModule, FormsModule, MemberReservationCard, PaginationComponent],
  templateUrl: './member-reservations.html',
  styleUrl: './member-reservations.scss',
})
export class MemberReservations implements OnInit, OnDestroy {
  showFilterPopup: boolean = false;
  reservations: CustomerReservationList[] = [];
  isLoading = false;
  currentPage = 0;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  
  searchTerm = '';
  private searchSubject = new Subject<string>();
  selectedStatus: string = 'all';
  selectedSort: string = 'createdAt,desc';
  private destroy$ = new Subject<void>();

  constructor(
    private reservationService: ClientReservationService,    
    private router: Router,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.loadReservations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 0;
      this.loadReservations();
    });
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.loadReservations();
  }

  onSortChange(): void {
    this.currentPage = 0;
    this.loadReservations();
  }

  loadReservations(): void {
    this.isLoading = true;
    
    const criteria: ReservationSearchCriteria = {};
    if (this.searchTerm.trim()) {
      criteria.search = this.searchTerm.trim();
    }

    const [sortField, sortDirection] = this.selectedSort.split(',');
    this.reservationService.getMyReservations(criteria, this.currentPage, this.pageSize, sortField, sortDirection)
      .subscribe({
        next: (response: ApiResponse<any>) => {
          this.reservations = response.data?.content || [];
          this.totalCount = response.data?.totalElements || 0;
          this.totalPages = response.data?.totalPages || 0;
          setTimeout(() => {
            this.isLoading = false;
          }, 500);
        },
        error: (error) => {
          console.error('Error loading blogs:', error);
          this.isLoading = false;
        }
      });
  }

  createReservation(): void {
    this.router.navigate(['/member/reservations/create']);
  }

  onReservationUpdated(): void {
    this.loadReservations();
  }

  onReservationDeleted(): void {
    this.loadReservations();
  }

  onPageChange(page: number) {
    this.isLoading = true;
    this.currentPage = page;
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setTimeout(() => {
      this.loadReservations();
    }, 1000);
  }
  
  toggleFilterPopup() {
    this.showFilterPopup = !this.showFilterPopup;
  }

  closeFilterPopup(event: Event) {
    if ((event.target as HTMLElement).classList.contains('filter-overlay')) {
      this.showFilterPopup = false;
    }
  }

  private updateQueryParams(params: any) {
    const queryParams = { ...this.route.snapshot.queryParams };
    
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === '') {
        delete queryParams[key];
      } else {
        queryParams[key] = params[key];
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
